<template>
  <v-container
    id="data-management"
    tag="section"
  >
    <base-resource-grid
      ref="resourceGrid"
      color="green"
      icon="mdi-clipboard-text-multiple"
      plural-name="Open Enrollments"
      singular-name="Open Enrollment"
      allow-expand
      fullscreen-edit
      :sub-title-source="subTitleSource"
      :headers="gridHeaders"
      :resource="openEnrollmentResource"
      :actions="actions"
      :allow-edit="allowEdit"
      :allow-delete="allowDelete"
      :fetch-method="getOpenEnrollments"
      :custom-columns="customColumns"
      :disabled="disabled"
      :sort-by="['oeStartDate']"
      @item-expanded="itemExpanded"
      @handleAction="handleAction"
    >
      <template
        v-slot:item.approvedPercent="{ item }"
      >
        <v-progress-circular
          :value="item.approvedPercent"
          :color="percentColor(item.approvedPercent)"
          size="35"
        >
          {{ item.approvedPercent }}
        </v-progress-circular>
      </template>
      <template
        v-slot:item.oeStartDate="{ item }"
      >
        <base-display-date
          v-model="item.oeStartDate"
        />
      </template>
      <template
        v-slot:gridInteractions
      >
        <v-switch
          v-if="showViewMine"
          v-model="viewMine"
          label="View Mine Only"
          @change="filtersChanged"
        />
        <base-date-picker
          :date-value.sync="oeStartDateFrom"
          label="OE Start (From)"
          @change="filtersChanged"
        />
        <base-date-picker
          :date-value.sync="oeStartDateTo"
          label="OE Start (To)"
          @change="filtersChanged"
        />
      </template>
      <template v-slot:rowExpand="bindings">
        <base-audit-container
          v-show="historyVisible(bindings.details.expandAction)"
          :item="bindings.details.item"
        />
        <group-summary
          v-show="bindings.details.expandAction === 'Group Details'"
          :details="bindings.details.item"
          :group-id="bindings.details.item.groupId"
        />
        <outstanding
          v-show="bindings.details.expandAction === 'Outstanding'"
          :open-enrollment="bindings.details.item"
          :disabled="disabled"
        />
      </template>
      <template v-slot:editForm="editForm">
        <v-row>
          <v-col
            cols="12"
          >
            <open-enrollment-wizard
              :edited-item="editForm.editedItem"
              @openenrollment:saved="openEnrollmentSaved"
              @wizard:finished="closeEditDialog"
              @summary:changed="summaryChanged"
            />
          </v-col>
        </v-row>
      </template>
      <template v-slot:actionButtons>
        <v-card-actions>
          <v-spacer />
        </v-card-actions>
      </template>
    </base-resource-grid>
    <open-enrollment-summary-dialog
      v-show="currentAction === 'Open Enrollment Summary'"
      :open-enrollment="selectedOpenEnrollment"
      @hide="selectedOpenEnrollment = null"
    />
    <renewal-dialog
      :open-enrollment="openEnrollmentToRenew"
      @closed="openEnrollmentToRenew = null"
      @renewal="handleRenewal"
    />
    <base-history-dialog
      id-field="openEnrollmentId"
      title-field="Open Enrollment"
      resource-endpoint="openEnrollmentHistory/"
      color="green"
      :columns="openEnrollmentHistoryColumns"
      :item="historyItem"
      @hide="historyItem = null"
    />
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import TroodyApi from '@/services/troody/TroodyApi'
  import { isAdmin, isGroup, isBroker, isProjectManager } from '@/services/roleValidator'

  export default {
    name: 'OpenEnrollments',

    components: {
      OpenEnrollmentWizard: () => import('./components/OpenEnrollmentWizard'),
      GroupSummary: () => import('./components/GroupSummary'),
      OpenEnrollmentSummaryDialog: () => import('./components/OpenEnrollmentSummaryDialog'),
      RenewalDialog: () => import('./components/RenewalDialog'),
      Outstanding: () => import('./components/Outstanding'),
    },

    data: () => ({
      historyTab: 'openEnrollment',

      customColumns: [
        'approvedPercent',
        'oeStartDate',
      ],

      subTitleSource: ['broker', 'groupName'],

      gridHeaders: [
        {
          text: 'Broker',
          value: 'broker',
        },
        {
          text: 'Group',
          value: 'groupName',
        },
        /*
        {
          text: 'Year',
          value: 'brokerGroupYear',
        },
        */
        {
          text: 'Start',
          value: 'oeStartDate',
        },
        {
          text: '% Done',
          value: 'approvedPercent',
        },
      ],

      openEnrollmentHistoryColumns: [
        {
          text: 'Is Renewal',
          value: 'isRenewal',
          dataType: 'boolean',
        },
        {
          text: 'Locked',
          value: 'locked',
          dataType: 'boolean',
        },
        {
          text: 'Broker',
          value: 'broker',
        },
        {
          text: 'Group',
          value: 'groupName',
        },
        {
          text: 'Year',
          value: 'brokerGroupYear',
        },
        {
          text: 'SIC Code',
          value: 'sicCode',
        },
        {
          text: 'Plan Start',
          value: 'planYearStartDate',
        },
        {
          text: 'Plan Ende',
          value: 'planYearEndDate',
        },
        {
          text: 'Self Service',
          value: 'selfService',
        },
        {
          text: 'Multi Year',
          value: 'multiYear',
        },
        {
          text: 'OE Start',
          value: 'oeStartDate',
        },
        {
          text: 'OE End',
          value: 'oeEndDate',
        },
        {
          text: 'Benefit Admin Close',
          value: 'selerixCloseDate',
        },
        {
          text: 'OE File Send',
          value: 'oeFileSendDate',
        },
        {
          text: 'Payroll File',
          value: 'uploadOrPayrollFile',
          dataType: 'boolean',
        },
        {
          text: 'Payroll File Type',
          value: 'uploadOrPayrollFileDesc',
        },
        {
          text: 'Payroll Deadline Broker',
          value: 'payrollDeadlineToBroker',
        },
        {
          text: 'Payroll Deadline Group',
          value: 'payrollDeadlineToGroup',
        },
        {
          text: 'Upload Assigned',
          value: 'uploadAssignedName',
        },
        {
          text: 'Platform',
          value: 'platform',
        },
        {
          text: 'Benefit Admin Site',
          value: 'benefitAdminSite',
        },
      ],

      openEnrollmentResource: new TroodyApi('openEnrollment/'),

      historyItem: null,

      currentAction: null,

      selectedOpenEnrollment: null,

      openEnrollmentToRenew: null,

      viewMine: false,

      oeStartDateFrom: null,

      oeStartDateTo: null,
    }),

    computed: {
      ...mapGetters('oidcStore', ['oidcUser']),

      showOutstanding () {
        return isGroup(this.oidcUser) === false
      },

      disabled () {
        return isProjectManager(this.oidcUser) === false
      },

      showViewMine () {
        return isAdmin(this.oidcUser)
      },

      allowEdit () {
        return isBroker(this.oidcUser)
      },

      allowDelete () {
        return isAdmin(this.oidcUser)
      },

      actions () {
        const actions = []

        if (isGroup(this.oidcUser)) {
          actions.push({
            color: 'info',
            icon: 'mdi-information-variant',
            action: 'history',
          })
          actions.push({
            color: 'green',
            icon: 'mdi-clipboard-text-multiple',
            action: 'Open Enrollment Summary',
          })
          actions.push({
            color: 'gray',
            icon: 'mdi-printer',
            action: 'Print',
          })
          actions.push({
            color: 'amber',
            icon: 'mdi-home-group',
            action: 'Group Details',
          })
        }

        if (isGroup(this.oidcUser)) {
          actions.push({
            color: 'red darken-4',
            icon: 'mdi-check-all',
            action: 'Outstanding',
          })
        }

        if (isProjectManager(this.oidcUser)) {
          actions.push({
            color: 'indigo',
            icon: 'mdi-refresh',
            action: 'renew',
          })
        }

        return actions
      },

      years () {
        const years = []
        let start = 2015
        const stop = this.currentYear() + 11
        while (start < stop) {
          years.push(start++)
        }
        return years
      },
    },

    created () {
      this.initialize()
    },

    methods: {
      initialize () {
        this.oeStartDateFrom = new Date()
        this.oeStartDateTo = new Date(new Date().getFullYear(), 11, 31)
      },

      currentYear () {
        return (new Date()).getFullYear()
      },

      percentColor (value) {
        let color = 'red'
        if (value > 33 && value < 66) {
          color = 'orange'
        } else if (value >= 66 && value < 100) {
          color = 'amber'
        } else if (value >= 100) {
          color = 'green'
        }
        return color
      },

      handleRenewal (data) {
        this.openEnrollmentToRenew = null
        this.openEnrollmentSaved(data)
        this.handleAction('Group Details', data)
        this.$refs.resourceGrid.editItem(data)
      },

      setHistoryTab (tab) {
        this.historyTab = tab
      },

      closeEditDialog () {
        this.$refs.resourceGrid.close()
      },

      reloadDynamicItemData (oe) {
        if (oe && oe.id) {
          this.openEnrollmentResource.get(oe.id)
            .then(data => {
              if (data) {
                oe.approvedPercent = data.approvedPercent
                this.$refs.resourceGrid.itemEdited(data)
              }
            })
        }
      },

      summaryChanged (oe) {
        this.reloadDynamicItemData(oe)
      },

      openEnrollmentSaved (data) {
        this.$refs.resourceGrid.itemEdited(data)
        this.reloadDynamicItemData()
      },

      historyVisible (action) {
        return !['Group Details', 'Outstanding'].includes(action)
      },

      handleAction (action, data, expandedItems) {
        if (action === 'renew') {
          this.openEnrollmentToRenew = data
        } else if (action === 'Print') {
          if (data && data.id) {
            const p = { openEnrollmentId: data.id, groupId: data.groupId }
            console.log(p)
            const printSummary = this.$router.resolve({ name: 'printOpenEnrollmentSummary', params: p })
            window.open(printSummary.href, '_blank')
          }
        } else {
          // If we are already on history, show details
          if (action === 'history' && this.currentAction === 'history') {
            if (expandedItems && expandedItems.length) {
              this.historyItem = data
            }
          } else if (action === 'Open Enrollment Summary') {
            this.selectedOpenEnrollment = data
          }

          this.$refs.resourceGrid.expand(action, data)
          this.currentAction = action
        }
      },

      itemExpanded (data) {
        if (data.value) {
          this.$refs.resourceGrid.expand(this.currentAction, data.item)
        } else {
          this.$refs.resourceGrid.collapse()
        }
      },

      getOpenEnrollments () {
        let from = null
        let to = null

        if (this.oeStartDateFrom) {
          const fromDate = new Date(this.oeStartDateFrom)
          from = `${fromDate.getMonth() + 1}_${fromDate.getDate()}_${fromDate.getFullYear()}`
        }

        if (this.oeStartDateTo) {
          const toDate = new Date(this.oeStartDateTo)
          to = `${toDate.getMonth() + 1}_${toDate.getDate()}_${toDate.getFullYear()}`
        }

        const payload = {
          viewMine: this.viewMine,
          oeStartDateRange: `${from}~${to}`,
        }

        return this.openEnrollmentResource.query(payload)
      },

      filtersChanged () {
        this.$refs.resourceGrid.refresh()
      },
    },
  }
</script>
